<template>
	<div class="flex flex-col gap-2 bg-white shadow sm:rounded-lg p-4 m-2">
		<div class="flex items-center justify-between w-full gap-2">
			<PageLabel :helpPageName="'Projects'">Projects</PageLabel>
			<div class="flex flex-row items-center gap-2">
				<Button id="btnNewUser" @click="newProject()" v-if="SessionInfo.hasRightsToScreen('Projects', PageAccess.Create)">
					<FontAwesomeIcon :icon="['far', 'briefcase']" />New Project
				</Button>
			</div>
		</div>
		<DataTable
			:value="filteredProjectsList"
			responsiveLayout="scroll"
			:row-hover="true"
			:paginator="true"
			:rows="tablePaginator == null ? 10 : tablePaginator.rows"
			:first="tablePaginator == null ? 0 : tablePaginator.first"
			:page="tablePaginator == null ? 0 : tablePaginator.page"
			@page="pageChanged($event)"
			paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
			:rowsPerPageOptions="[10, 20, 50]"
			currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
			dataKey="id"
			v-model:filters="usersFilters"
			filterDisplay="menu"
			@filter="applyFilter($event)"
			:loading="loading"
			:globalFilterFields="['projectName', 'subProjectsNum', 'projectCollaboratorList']"
			id="dtUserList"
			:sortField="sortField"
			:sortOrder="sortOrder"
		>
			<template #header>
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-2">
						<Button icon="pi pi-filter-slash" label="Clear" @click="usersFilters['global'].value = null" />
						<span class="relative">
							<i class="pi pi-search absolute inset-y-3 right-3 flex items-center pointer-events-none"></i>
							<InputText class="pl-10" v-model="usersFilters['global'].value" placeholder="Keyword Search" id="UserKeywordSearch" />
						</span>
					</div>
					<div v-if="isTemplateCopyEnabled" class="flex items-center gap-2">
						<span class="text-sm text-gray-600">Show templates only</span>
						<ToggleSwitch v-model="showTemplatesOnly" />
					</div>
				</div>
			</template>
			<template #empty> No project found. </template>
			<template #loading> Loading projects data. Please wait. </template>
			<Column field="projectName" filterField="projectName" header="Project Name" :sortable="true" style="flex: 0 0 30%">
				<template #body="{ data }">
					<div class="w-full flex items-center gap-2" @click="selectProject(data)">
						{{ data.projectName }}
						<span
							v-if="isTemplateCopyEnabled && data.project.isTemplateProject"
							class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full border border-yellow-300"
							>Template</span
						>
					</div>
				</template>
			</Column>
			<Column field="subProjectsNum" filterField="subProjectsNum" header="Subproject Count" :sortable="true" style="flex: 0 0 5%">
				<template #body="{ data }">
					<!-- <div class="w-full" @click="selectProject(data)">{{ Object.keys(data.subProject).length }}</div> -->
					<div class="w-full" @click="selectProject(data)">{{ data.subProjectsNum }}</div>
				</template>
			</Column>
			<Column
				field="projectCollaboratorList"
				filterField="projectCollaboratorList"
				header="Project Collaborators"
				:sortable="true"
				style="flex: 0 0 45%"
			>
				<template #body="{ data }">
					<div class="w-full" @click="selectProject(data)">
						<!-- {{ data.projectCollaborator ? collaboratorsList(Object.values(data.projectCollaborator)) : "" }} -->
						{{ data.projectCollaboratorList }}
					</div>
				</template>
			</Column>
			<!-- <Column field="project.createdTimeStamp" filterField="createdTimeStamp" header="Project Creation Date" :sortable="true" style="flex: 0 0 45%">
				<template #body="{ data }">
					<div class="w-full" @click="selectProject(data)">
						{{ data.projectCollaborator ? collaboratorsList(Object.values(data.projectCollaborator)) : "" }} 
						{{ useDateTimeHelper(data.project.createdTimeStamp).toLocalTimeZoneStandardFormat() }}
					</div>
				</template>
			</Column> -->
			<Column header="Created" sortable style="flex: 0 0 45%">
				<template #body="{ data }">
					<div class="w-full" @click="selectProject(data)">
						<I2TimestampDisplay :date="data.project.createdTimeStamp" :user="data.project.createdByPerson" />
					</div>
				</template>
			</Column>
			<Column field="project.updatedTimeStamp" filterField="updatedTimeStamp" header="Last Modified" :sortable="true" style="flex: 0 0 45%">
				<template #body="{ data }">
					<div class="w-full" @click="selectProject(data)">
						<!-- {{ data.projectCollaborator ? collaboratorsList(Object.values(data.projectCollaborator)) : "" }} -->
						<!-- {{ useDateTimeHelper(data.project.updatedTimeStamp).toLocalTimeZoneStandardFormat() }} -->
						<I2TimestampDisplay :date="data.project.updatedTimeStamp" :user="data.project.updatedByPerson" />
					</div>
				</template>
			</Column>
			<Column field="id" header="Actions" style="width: 11%" styleClass="tdLessPadding">
				<template #body="slotProps"
					><span style="cursor: pointer"
						><FontAwesomeIcon
							:icon="['far', 'pen']"
							title="Modify"
							@click="editProject(slotProps.data.project)"
							v-show="SessionInfo.hasRightsToScreen('Projects', PageAccess.Update)" /></span
					><span style="cursor: pointer"
						><FontAwesomeIcon
							:icon="['far', 'info-circle']"
							title="View"
							@click="editProject(slotProps.data.project)"
							class="ml-2"
							v-show="!SessionInfo.hasRightsToScreen('Projects', PageAccess.Update)" /></span
					><span style="cursor: pointer"
						><FontAwesomeIcon
							:icon="['far', 'trash']"
							title="Delete"
							@click="deleteProject(slotProps.data.project)"
							class="ml-2"
							v-show="SessionInfo.hasRightsToScreen('Projects', PageAccess.Delete)" /></span
					><span
						style="cursor: pointer"
						v-show="isTemplateCopyEnabled && SessionInfo.currentUserIsAdmin()"
						v-tooltip.top="
							slotProps.data.project.isTemplateProject
								? 'This is the Template Intermediates project. Click to remove.'
								: 'Set as Template Intermediates project for copy step templates.'
						"
						><FontAwesomeIcon
							:icon="slotProps.data.project.isTemplateProject ? ['fas', 'postage-stamp'] : ['far', 'postage-stamp']"
							@click="setAsTemplateProject(slotProps.data.project)"
							class="ml-2"
							:class="{ 'text-yellow-500': slotProps.data.project.isTemplateProject }" /></span></template
			></Column>
		</DataTable>
		<div v-if="showDialog">
			<projectEdit :showDialog="true" :project="project" @cancel="cancel()" @save="save()" />
		</div>

		<Dialog v-model:visible="showDialogDelete" :modal="true" style="width: 35vw" :closable="false" contentStyle="overflow:visible">
			<template #header>
				<PageLabel :helpPageName="'projectDelete'">Delete Project </PageLabel>
			</template>
			<FormCard>
				<I2FormRow class="mt-6">
					<h1>Are you sure you would like to delete selected Project?</h1>
				</I2FormRow>
			</FormCard>
			<template #footer>
				<I2FormRow right>
					<Button label="Cancel" class="inverted mr-4" @click="showDialogDelete = false" right />
					<Button label="Delete" class="ml-4" @click="deleteProjectOK()" right />
				</I2FormRow>
			</template>
		</Dialog>
	</div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, reactive } from "vue"
import Dialog from "primevue/dialog"
import { ApiClient, Project, FeatureFlagClient } from "../../entities/NSwagGenerated"
import { EventBus } from "../eventBus"

import { FilterMatchMode, FilterOperator } from "@primevue/core/api"
import Button from "primevue/button"
import InputText from "primevue/inputtext"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import PageLabel from "./pageLabel.vue"
import DataTable, { DataTablePageEvent, DataTableFilterEvent } from "primevue/datatable"
import Column from "primevue/column"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faInfoCircle, faTrash, faPen, faBriefcase, faPostageStamp } from "@fortawesome/pro-regular-svg-icons"
import { faPostageStamp as faPostageStampSolid } from "@fortawesome/pro-solid-svg-icons"
library.add(faTrash, faPen, faInfoCircle, faBriefcase, faPostageStamp, faPostageStampSolid)
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import projectEdit from "./projectEdit.vue"
import { useDateTimeHelper } from "@/composables/dateTimeHelper"
import { useRouter, useRoute } from "vue-router"
import { useToast } from "primevue/usetoast"
import I2TimestampDisplay from "./I2TimestampDisplay.vue"
import ToggleSwitch from "primevue/toggleswitch"
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())
const toast = useToast()
const projectsList = ref()
const showTemplatesOnly = ref(false)
const isTemplateCopyEnabled = ref(false)
const filteredProjectsList = computed(() => {
	if (!showTemplatesOnly.value || !projectsList.value) return projectsList.value
	return projectsList.value.filter((p: any) => p.project?.isTemplateProject)
})
let showDialog = ref(false)
let showDialogDelete = ref(false)
let tablePaginator: any | null = null
const selectedProjectId = ref()
let loading = ref(false)
const router = useRouter()
const route = useRoute()
let project = new Project()

const sortField = ref("")
const sortOrder = ref(1)

let usersFilters = reactive({
	global: { value: null, matchMode: FilterMatchMode.CONTAINS },
	projectName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
	ProjectCollaborator: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
})

onMounted(async () => {
	EventBus.emit("showBreadcrumb", "reset")
	refresh()
	try {
		const flag = await featureFlagClient.getByName("Use Template in Step Copy in Synthetic API")
		isTemplateCopyEnabled.value = flag.isEnabled || false
	} catch {
		isTemplateCopyEnabled.value = false
	}
})
const save = () => {
	showDialog.value = false
	refreshOnSave()
}
const cancel = () => {
	selectedProjectId.value = null
	showDialog.value = false
	//refresh()
}
const newProject = () => {
	selectedProjectId.value = null
	project = new Project()
	showDialog.value = true
}
function deleteProject(data: Project) {
	selectedProjectId.value = data.id
	showDialogDelete.value = true
}
function deleteProjectOK() {
	apiClient.projectDelete(selectedProjectId.value).then(
		(result) => {
			refresh()
			showDialogDelete.value = false
		},
		(err) => {
			throw err
		}
	)
}
function selectProject(data: Project) {
	selectedProjectId.value = data.id
	// 	type: "project",
	// 	label: data.projectName,
	// 	id: data.id,
	// })
	router.push({ name: "subProjectList", query: { projectId: data.id } })
}
function editProject(data: any) {
	project = data
	showDialog.value = true
}

async function setAsTemplateProject(data: Project) {
	const newValue = !data.isTemplateProject
	try {
		await apiClient.setTemplateProject(data.id!, newValue)
		const message = newValue ? `"${data.projectName}" set as template project` : `"${data.projectName}" removed as template project`
		toast.add({ severity: "success", summary: "Success", detail: message, life: 3000 })
		refresh()
	} catch (err) {
		toast.add({ severity: "error", summary: "Error", detail: "Failed to update template project", life: 3000 })
	}
}

function pageChanged(ev: DataTablePageEvent) {
	tablePaginator = ev
	localStorage.setItem("projectListPaginate", JSON.stringify(ev))
}

function applyFilter(ev: DataTableFilterEvent) {
	localStorage.setItem("tableUserFilter", JSON.stringify(usersFilters))
}

function refresh() {
	loading.value = true
	apiClient!
		.getProjectsForTable()
		.then(
			(result) => {
				projectsList.value = result
			},
			(err) => {
				throw err
			}
		)
		.then(() => {
			loading.value = false
		})
}
function refreshOnSave() {
	apiClient!
		.getProjectsForTable()
		.then(
			(result) => {
				projectsList.value = result
			},
			(err) => {
				throw err
			}
		)
		.then(() => {
			sortField.value = "project.createdTimeStamp"
			sortOrder.value = -1
		})
}
</script>
